import React, { Fragment, useCallback, useState } from 'react';
import Dropzone from 'react-dropzone';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { CircularProgress, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { useMessageContext } from 'UI/contexts';
import { useT } from 'UI/lib';

import { styles } from './dropZone.styles';

const types = [
  { mime: 'application/pdf', ext: 'pdf' },
  { mime: 'application/msword', ext: 'doc' },
  { mime: 'application/x-msi', ext: 'msi' },
  { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ext: 'docx' },
];

const spreadsheetTypes = [
  {
    mime: 'text/csv',
    ext: 'csv',
  },
];

const maxSize = 5242880;

const useStyles = makeStyles(styles);

const DropZone = ({
  onFile,
  loading,
  defaultType = true,
  supportedExtText = 'pdf, doc, docx',
  dropFileHelper = 'restrict:scan_document_modal.drop_file_helper',
  maxSizeExceedInfo,
  defaultErrorInfo = 'restrict:scan_document_modal.default_error',
  dataTest = 'scan_document-drop_zone',
}) => {
  const classes = useStyles();
  const t = useT();
  const message = useMessageContext();

  const [file, setFile] = useState(null);

  const acceptTypes = {
    accept: defaultType ? types.map(type => type.mime) : spreadsheetTypes.map(type => type.mime),
  };

  const onDrop = useCallback(files => {
    onFile(files[0]);
    setFile(files);
  });

  const onDropRejected = useCallback(files => {
    const file = files[0];
    if (file.size > maxSize) {
      message.setMessage({
        type: 'info',
        text: maxSizeExceedInfo ?? t('restrict:scan_document_modal.max_upload_size', { size: 5 }),
      });
    } else {
      message.setMessage({ type: 'info', text: t(defaultErrorInfo) });
    }
  });

  return (
    <Dropzone
      accept={acceptTypes}
      onDropAccepted={onDrop}
      onDropRejected={onDropRejected}
      className={classes.root}
      multiple={false}
      maxSize={maxSize}
      disablePreview
      data-test={dataTest}
    >
      {({ getRootProps, getInputProps }) => (
        <Grid container direction="column" textAlign="center" {...getRootProps()}>
          {loading && file ? (
            <Fragment>
              <Grid item>
                <p>
                  <small>
                    <strong>
                      {t('common:loading')} {file[0]?.name}...
                    </strong>
                  </small>
                </p>
              </Grid>
              <Grid item pt={10}>
                <CircularProgress />
              </Grid>
            </Fragment>
          ) : (
            <Fragment>
              <Grid item xs pt={20}>
                <CloudUploadIcon style={{ fontSize: 75 }} />
                <p>{t(dropFileHelper)}</p>
                <input {...getInputProps()} />
              </Grid>
              <Grid item>
                <p>
                  <small>
                    <strong>{t('restrict:common.supported_extensions')}</strong>: {supportedExtText}
                    .
                  </small>
                </p>
              </Grid>
            </Fragment>
          )}
        </Grid>
      )}
    </Dropzone>
  );
};

export default DropZone;
